import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import {
  buscarAgrupadoEsocial,
  buscarCalculo20sEsocial,
  buscarDashboardEsocial,
  buscarRubricasEsocial,
  atualizarRubricaEsocial,
  buscarBasesLegais,
  exportarEsocial,
  reprocessarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import { listarProjetosDoCliente, salvarProjeto } from "../../../services/projetos-apuracao.service";
import {
  usePermissoes,
  CHAVES_APURACOES_EDIT,
} from "../../../config/Context/PermissoesContext";
import { lerParametrosProjeto, montarUrlApuracao } from "../../ProjetosApuracao/utils/url.util";
import { selecaoEsocial } from "../../ProjetosApuracao/utils/selecao.util";
import ModalSalvarProjeto from "../../ProjetosApuracao/components/ModalSalvarProjeto";
import SeletorProjetosCliente from "../../ProjetosApuracao/components/SeletorProjetosCliente";
import { fmtMesAno } from "../utils";
import { formatCurrency, formatValue } from "../../../utils/formatValue";
import SnackBar from "../../../components/SnackBar";
import BotaoExportacaoProgresso, {
  rotuloDoEstado,
} from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ModalAtualizarSelicApuracao from "../ModalAtualizarSelicApuracao";
import {
  competenciaRangeFromLinhasEsocial,
  filtrarLinhasEsocialAgrupado,
  paginarLinhas,
  tiposRubricaFromLinhas,
} from "./utils/filtrarLinhasEsocial.util";
import { opcoesBaseLegal } from "./utils/baseLegalMultiSelect.util";
import FiltroBaseLegalMultiSelect from "./components/FiltroBaseLegalMultiSelect";
import BarraGruposColunas from "./components/BarraGruposColunas";
import CabecalhoRubricas from "./components/CabecalhoRubricas";
import CelulasIncidenciaRubrica from "./components/CelulasIncidenciaRubrica";
import CelulaVigencia from "./components/CelulaVigencia";
import DropdownBaseLegal from "./components/DropdownBaseLegal";
import DropdownSimNao from "./components/DropdownSimNao";
import { escalaDasRubricas, formatarMesAno } from "./utils/vigenciaRubrica.util";
import {
  GRUPOS_PADRAO_VISIVEIS,
  colunasVisiveisRubricas,
} from "./utils/gruposColunasRubricas.util";
import {
  agregarDashboardEsocial,
  ordenarLinhasEsocialAgrupado,
} from "./utils/ordenarLinhasEsocial.util";
import { competenciaParaYm } from "./utils/competenciaYm.util";
import {
  buildCatalogo20s,
  buildCatalogoRubricas,
  enriquecerRubricas,
} from "./utils/catalogosFiltro.util";
import FiltroCatalogoDinamico, {
  useFiltroCatalogoDinamico,
} from "../../../components/FiltroCatalogoDinamico";
import styles from "./detalheEsocial.styles.module.css";

/** Vistas secundárias acessíveis só pelo header (sem barra de abas). */
const VISTAS_HEADER = [
  { id: "rubricas", label: "Rúbricas" },
  { id: "20s", label: "Cálculo 20S" },
];

const DOMINIOS_INCIDENCIA_VAZIOS = { inss: [], fgts: [], irrf: [] };

/** Variantes do menu Exportar — cada uma tem sua instância de useExportacaoAssincrona. */
const ITENS_MENU_EXPORT = [
  { tipo: "agrupado", rotulo: "Exportar Agrupado" },
  { tipo: "lancamentos", rotulo: "Exportar Lançamentos" },
  { tipo: "resumo_geral", rotulo: "Exportar Resumo Geral" },
];

function fmtCompetencia(value) {
  if (!value) return "—";
  const s = String(value);
  return s.length >= 7 ? s.slice(0, 7) : s;
}

function fmtCompetenciaPt(value) {
  const ym = fmtCompetencia(value);
  if (ym === "—" || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  return `${m}/${y}`;
}

function FlagBadge({ sim }) {
  return (
    <span className={sim ? styles.badgeSim : styles.badgeNao}>{sim ? "Sim" : "Não"}</span>
  );
}

/** Sim/Não editável da lista: seletor compacto no visual dos filtros do sistema. */
function FlagSelect({ value, disabled, onChange, "aria-label": ariaLabel }) {
  return (
    <DropdownSimNao value={value} disabled={disabled} onChange={onChange} ariaLabel={ariaLabel} />
  );
}

/**
 * Seletor de base legal da rubrica. Substituiu o FlagSelect de "Composição do Projeto":
 * a rubrica compõe o projeto exatamente quando tem base legal.
 */
function BaseLegalSelect({
  baseLegalId,
  baseLegalDescricao,
  basesLegais,
  disabled,
  onChange,
  "aria-label": ariaLabel,
}) {
  return (
    <DropdownBaseLegal
      baseLegalId={baseLegalId}
      baseLegalDescricao={baseLegalDescricao}
      basesLegais={basesLegais}
      disabled={disabled}
      onChange={onChange}
      ariaLabel={ariaLabel}
    />
  );
}

function celulaOuTraco(valor) {
  if (valor == null || String(valor).trim() === "") return "—";
  return String(valor);
}

function SortTh({ col, label, ordenacao, onSort, className = "" }) {
  const active = ordenacao.col === col;
  const cls = [
    styles.sortable,
    className,
    active ? (ordenacao.dir === "asc" ? styles.sortedAsc : styles.sortedDesc) : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <th
      className={cls}
      scope="col"
      data-sort-col={col}
      onClick={() => onSort(col)}
      aria-sort={active ? (ordenacao.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
    </th>
  );
}

/** Legenda dos cards de dashboard — mostra no máximo 3 linhas; acima disso, uma seta
 * expande a lista completa (empurrando o conteúdo abaixo). */
function LegendaColapsavel({ itens, renderItem, ariaLabel }) {
  const [expandido, setExpandido] = useState(false);
  const lista = Array.isArray(itens) ? itens : [];
  const temMais = lista.length > 3;
  const visiveis = expandido ? lista : lista.slice(0, 3);
  return (
    <>
      <div className={styles.dashLegend}>{visiveis.map(renderItem)}</div>
      {temMais && (
        <button
          type="button"
          className={styles.dashLegendToggle}
          onClick={() => setExpandido((v) => !v)}
          aria-expanded={expandido}
          aria-label={expandido ? `Recolher ${ariaLabel}` : `Expandir ${ariaLabel}`}
        >
          <span
            className={`${styles.dashLegendToggleIcon} ${
              expandido ? styles.dashLegendToggleIconOpen : ""
            }`}
            aria-hidden
          >
            ▼
          </span>
          {expandido ? "Ver menos" : `Ver mais (${lista.length - 3})`}
        </button>
      )}
    </>
  );
}

function MetricCard({ label, total, serie, campo }) {
  const lista = Array.isArray(serie) ? serie : [];
  const soma = lista.reduce((acc, s) => acc + (Number(s[campo]) || 0), 0);
  return (
    <div className={styles.dashCard}>
      <div className={styles.dashLbl}>{label}</div>
      <div className={styles.dashHint}>Por ano (soma das competências no filtro)</div>
      <div className={styles.dashVal}>{formatCurrency(total ?? 0)}</div>
      {lista.length > 0 && (
        <>
          <div
            className={styles.dashStack}
            role="img"
            aria-label={`Distribuição por ano — ${label}`}
          >
            {soma <= 0 ? (
              <span style={{ flex: 1, background: "#e0e4ea" }} title="Sem valores no filtro" />
            ) : (
              lista.map((s) => {
                const v = Number(s[campo]) || 0;
                const pct = Math.max(0, Math.min(100, (v / soma) * 100));
                return (
                  <span
                    key={`${s.ano}-${campo}`}
                    style={{
                      flex: `${Math.max(pct, 0.5)} 1 0%`,
                      minWidth: 4,
                      background: s.cor,
                    }}
                    title={`${s.ano}: ${formatCurrency(v)} (${pct.toLocaleString("pt-BR", {
                      maximumFractionDigits: 1,
                    })}%)`}
                  />
                );
              })
            )}
          </div>
          <LegendaColapsavel
            itens={lista}
            ariaLabel={`legenda de ${label}`}
            renderItem={(s) => {
              const v = Number(s[campo]) || 0;
              const pct = soma > 0 ? (v / soma) * 100 : 0;
              return (
                <span key={`leg-${s.ano}-${campo}`}>
                  <span className={styles.yearDot} style={{ background: s.cor }} />
                  <strong>{s.ano}</strong> {formatCurrency(v)}{" "}
                  <span className={styles.dashLegendPct}>
                    ({pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)
                  </span>
                </span>
              );
            }}
          />
        </>
      )}
    </div>
  );
}

/** Variante de MetricCard para séries já agregadas por grupo (não por ano) — usada
 * pelos cards "Totais por Base Legal" e "Totais por Tipo de Base Legal" (TXM-480). */
function GroupedMetricCard({ label, hint, total, grupos }) {
  const lista = Array.isArray(grupos) ? grupos : [];
  const soma = lista.reduce((acc, g) => acc + (Number(g.valor) || 0), 0);
  return (
    <div className={styles.dashCard}>
      <div className={styles.dashLbl}>{label}</div>
      <div className={styles.dashHint}>{hint}</div>
      <div className={styles.dashVal}>{formatCurrency(total ?? 0)}</div>
      {lista.length > 0 && (
        <>
          <div className={styles.dashStack} role="img" aria-label={`Distribuição — ${label}`}>
            {soma <= 0 ? (
              <span style={{ flex: 1, background: "#e0e4ea" }} title="Sem valores no filtro" />
            ) : (
              lista.map((g) => {
                const v = Number(g.valor) || 0;
                const pct = Math.max(0, Math.min(100, (v / soma) * 100));
                return (
                  <span
                    key={g.chave}
                    style={{
                      flex: `${Math.max(pct, 0.5)} 1 0%`,
                      minWidth: 4,
                      background: g.cor,
                    }}
                    title={`${g.rotulo}: ${formatCurrency(v)} (${pct.toLocaleString("pt-BR", {
                      maximumFractionDigits: 1,
                    })}%)`}
                  />
                );
              })
            )}
          </div>
          <LegendaColapsavel
            itens={lista}
            ariaLabel={`legenda de ${label}`}
            renderItem={(g) => {
              const v = Number(g.valor) || 0;
              const pct = soma > 0 ? (v / soma) * 100 : 0;
              return (
                <span key={`leg-${g.chave}`}>
                  <span className={styles.yearDot} style={{ background: g.cor }} />
                  <strong>{g.rotulo}</strong> {formatCurrency(v)}{" "}
                  <span className={styles.dashLegendPct}>
                    ({pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)
                  </span>
                </span>
              );
            }}
          />
        </>
      )}
    </div>
  );
}

function filtrosPadrao(linhas) {
  const { min, max } = competenciaRangeFromLinhasEsocial(linhas);
  return {
    competenciaInicio: min || "",
    competenciaFim: max || "",
    codigoRubrica: "",
    buscaRubrica: "",
    tipoRubrica: "",
    baseLegalSelecionadas: [],
    flagIncidencia: "",
    verbaFerias: "",
  };
}

function PaginationBar({ id, paginacao, limite, onLimiteChange, onPaginaChange }) {
  if (!paginacao || paginacao.total <= 0) return null;
  const { pagina, total, totalPaginas } = paginacao;
  return (
    <div className={styles.paginationBar}>
      <span className={styles.paginationInfo}>
        Mostrando {(pagina - 1) * paginacao.limite + 1}–
        {Math.min(pagina * paginacao.limite, total)} de {total} registro
        {total !== 1 ? "s" : ""} · página {pagina} de {Math.max(1, totalPaginas)}
      </span>
      <div className={styles.paginationControls}>
        <div className={styles.pgSizeGroup}>
          <label htmlFor={id} className={styles.pgSizeLabel}>
            Itens por página
          </label>
          <select
            id={id}
            className={styles.pageSizeSelect}
            value={limite}
            onChange={(e) => onLimiteChange(Number(e.target.value))}
            aria-label="Itens por página"
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          type="button"
          className={styles.btnPage}
          disabled={pagina <= 1}
          onClick={() => onPaginaChange(Math.max(1, pagina - 1))}
        >
          Anterior
        </button>
        <button
          type="button"
          className={styles.btnPage}
          disabled={pagina >= (totalPaginas || 1)}
          onClick={() => onPaginaChange(pagina + 1)}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}

const ApuracaoDetalheEsocial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pode } = usePermissoes();
  const podeEditar = pode(CHAVES_APURACOES_EDIT);
  const [aba, setAba] = useState("detalhe");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [agrupado, setAgrupado] = useState([]);
  // null = ainda não carregado nesta sessão (mostra o loading da aba); depois do
  // primeiro carregamento, cada reentrada na aba busca de novo e sobrescreve em
  // seguida, sem "piscar" para null de novo — ver efeito guiado por `aba` abaixo.
  const [rubricas, setRubricas] = useState(null);
  // TXM-671: listas oficiais de incidência (droplists) e grupos de colunas exibidos na
  // aba Rúbricas. Os grupos não são persistidos: toda entrada na aba volta a só INSS.
  const [dominiosIncidencia, setDominiosIncidencia] = useState(DOMINIOS_INCIDENCIA_VAZIOS);
  const [gruposVisiveis, setGruposVisiveis] = useState(GRUPOS_PADRAO_VISIVEIS);
  const [basesLegais, setBasesLegais] = useState([]);
  const [calculo20s, setCalculo20s] = useState(null);
  const [salvandoRubrica, setSalvandoRubrica] = useState({});
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [menuExportOpen, setMenuExportOpen] = useState(false);
  const menuExportRef = useRef(null);
  const [reprocessando, setReprocessando] = useState(false);
  const [modalAtualizarSelic, setModalAtualizarSelic] = useState(false);
  const [filtros, setFiltros] = useState(filtrosPadrao([]));
  const [filtrosExpandido, setFiltrosExpandido] = useState(true);
  const [paginaDetalhe, setPaginaDetalhe] = useState(1);
  const [limiteDetalhe, setLimiteDetalhe] = useState(10);
  const [paginaRubricas, setPaginaRubricas] = useState(1);
  const [limiteRubricas, setLimiteRubricas] = useState(10);
  const [pagina20s, setPagina20s] = useState(1);
  const [limite20s, setLimite20s] = useState(10);
  const [ordenacao, setOrdenacao] = useState({ col: "competencia", dir: "asc" });
  const [projetosCliente, setProjetosCliente] = useState([]);
  const [modalSalvarProjeto, setModalSalvarProjeto] = useState(false);
  const [salvandoProjeto, setSalvandoProjeto] = useState(false);

  // TXM-558 — `?pf=` reaplica os filtros salvos de um projeto (uma vez, depois da primeira carga) e
  // `?projeto=` abre o modo consulta dos itens utilizados por aquele projeto aprovado.
  const { filtros: filtrosSalvosUrl, idProjeto: projetoFiltro } = useMemo(
    () => lerParametrosProjeto(location.search),
    [location.search]
  );
  const pfBruto = new URLSearchParams(location.search).get("pf");
  const pfNonce = location.state?.pfNonce;
  const ultimoPfAplicado = useRef({ pf: null, nonce: undefined });

  // Contadores de requisição por aba: descartam resposta obsoleta quando o usuário
  // troca de aba rápido o bastante para duas buscas da mesma aba ficarem em voo.
  const detalheReqRef = useRef(0);
  const rubricasReqRef = useRef(0);
  const calculo20sReqRef = useRef(0);

  const carregar = useCallback(async () => {
    const reqId = ++detalheReqRef.current;
    setLoading(true);
    setErro("");
    const opcoesProjeto = projetoFiltro ? { projeto: projetoFiltro } : null;
    const [resDashboard, resAgrupado] = await Promise.all([
      opcoesProjeto ? buscarDashboardEsocial(id, opcoesProjeto) : buscarDashboardEsocial(id),
      opcoesProjeto ? buscarAgrupadoEsocial(id, opcoesProjeto) : buscarAgrupadoEsocial(id),
    ]);
    if (detalheReqRef.current !== reqId) return;

    if (!resDashboard.sucesso) {
      setErro(resDashboard.mensagem || "Não foi possível carregar a apuração eSocial.");
      setLoading(false);
      return;
    }

    const linhas = resAgrupado.sucesso ? resAgrupado.linhas : [];
    setApuracao(resDashboard.apuracao);
    setAgrupado(linhas);
    setFiltros(filtrosPadrao(linhas));
    setPaginaDetalhe(1);
    setLoading(false);
  }, [id, projetoFiltro]);

  const carregarRubricas = useCallback(async () => {
    const reqId = ++rubricasReqRef.current;
    const res = await buscarRubricasEsocial(id);
    if (rubricasReqRef.current !== reqId) return;
    setRubricas(res.sucesso ? res.linhas : []);
    setDominiosIncidencia(res.sucesso && res.dominios ? res.dominios : DOMINIOS_INCIDENCIA_VAZIOS);
  }, [id]);

  const carregarCalculo20s = useCallback(async () => {
    const reqId = ++calculo20sReqRef.current;
    const res = await buscarCalculo20sEsocial(id);
    if (calculo20sReqRef.current !== reqId) return;
    setCalculo20s(res.sucesso ? res : null);
  }, [id]);

  // Catálogo de bases legais: independente da apuração, carregado uma vez por tela.
  // Falha aqui não bloqueia o detalhe — o seletor fica sem opções.
  useEffect(() => {
    let ativo = true;
    buscarBasesLegais("ESOCIAL").then((res) => {
      if (!ativo) return;
      setBasesLegais(res.basesLegais || []);
    });
    return () => {
      ativo = false;
    };
  }, []);

  // Reaplica os filtros salvos de um projeto (`?pf=`) por cima do padrão, depois que a carga termina.
  // Só reaplica quando o `pf` muda ou quando o usuário escolhe de novo um projeto em "Projetos deste
  // cliente" (`pfNonce`); trocar de aba e voltar não pode sobrescrever os filtros que ele tinha.
  // Campos que a tela não conhece (projeto salvo em versão anterior) são ignorados.
  useEffect(() => {
    if (!apuracao || !filtrosSalvosUrl) return;
    const anterior = ultimoPfAplicado.current;
    const mudou = anterior.pf !== pfBruto || (pfNonce !== undefined && pfNonce !== anterior.nonce);
    if (!mudou) return;
    ultimoPfAplicado.current = { pf: pfBruto, nonce: pfNonce };
    const padrao = filtrosPadrao(agrupado);
    const conhecidos = Object.fromEntries(
      Object.entries(filtrosSalvosUrl).filter(([campo]) => campo in padrao)
    );
    setFiltros({ ...padrao, ...conhecidos });
  }, [apuracao, agrupado, filtrosSalvosUrl, pfBruto, pfNonce]);

  // Projetos do cliente (TXM-558): alimenta o seletor "Projeto" e "Projetos deste cliente".
  // Falha aqui não bloqueia o detalhe.
  const cnpjApuracao = apuracao?.cnpj;
  const carregarProjetosCliente = useCallback(async () => {
    if (!cnpjApuracao) return;
    const res = await listarProjetosDoCliente({ cnpj: cnpjApuracao, tipo: "esocial" });
    setProjetosCliente(res.sucesso && Array.isArray(res.projetos) ? res.projetos : []);
  }, [cnpjApuracao]);

  useEffect(() => {
    carregarProjetosCliente();
  }, [carregarProjetosCliente]);

  const projetosAprovados = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "APROVADO"),
    [projetosCliente]
  );
  const projetosEmPrevia = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "EM_PREVIA"),
    [projetosCliente]
  );
  const codigoDoProjeto = (idProjeto) =>
    projetosCliente.find((projeto) => projeto.idProjeto === idProjeto)?.codigo || `#${idProjeto}`;

  const handleProjetoChange = (idProjeto) => {
    navigate(idProjeto ? `${location.pathname}?projeto=${idProjeto}` : location.pathname, {
      replace: true,
    });
  };

  const tiposRubrica = useMemo(() => tiposRubricaFromLinhas(agrupado), [agrupado]);

  const linhasFiltradas = useMemo(() => {
    const filtradas = filtrarLinhasEsocialAgrupado(agrupado, filtros);
    return ordenarLinhasEsocialAgrupado(filtradas, ordenacao);
  }, [agrupado, filtros, ordenacao]);

  const dash = useMemo(
    () => agregarDashboardEsocial(linhasFiltradas, basesLegais),
    [linhasFiltradas, basesLegais]
  );

  const totalLancamentos = useMemo(
    () =>
      linhasFiltradas.reduce((acc, l) => acc + (Number(l.qtdLancamentos) || 0), 0),
    [linhasFiltradas]
  );

  const { linhas: linhasPagina, paginacao: pagDetalhe } = useMemo(
    () => paginarLinhas(linhasFiltradas, paginaDetalhe, limiteDetalhe),
    [linhasFiltradas, paginaDetalhe, limiteDetalhe]
  );

  const rubricasEnriquecidas = useMemo(() => enriquecerRubricas(rubricas), [rubricas]);
  // Escala da mini linha do tempo da coluna "Período de vigência" (período da apuração).
  const escalaVigencia = useMemo(
    () => escalaDasRubricas(rubricasEnriquecidas, apuracao),
    [rubricasEnriquecidas, apuracao]
  );

  const catalogoRubricas = useMemo(
    () => buildCatalogoRubricas(rubricasEnriquecidas, basesLegais, dominiosIncidencia),
    [rubricasEnriquecidas, basesLegais, dominiosIncidencia]
  );
  const filtroRubricas = useFiltroCatalogoDinamico(catalogoRubricas);
  const rubricasFiltradas = useMemo(
    () => filtroRubricas.apply(rubricasEnriquecidas),
    [filtroRubricas.apply, rubricasEnriquecidas]
  );

  const { linhas: rubricasPagina, paginacao: pagRubricas } = useMemo(
    () => paginarLinhas(rubricasFiltradas, paginaRubricas, limiteRubricas),
    [rubricasFiltradas, paginaRubricas, limiteRubricas]
  );

  const catalogo20s = useMemo(() => buildCatalogo20s(), []);
  const filtro20s = useFiltroCatalogoDinamico(catalogo20s);
  const linhas20sFiltradas = useMemo(
    () => filtro20s.apply(calculo20s?.linhas ?? []),
    [filtro20s.apply, calculo20s]
  );
  const valorAtualizado20sFiltrado = useMemo(
    () => linhas20sFiltradas.reduce((acc, l) => acc + (Number(l.valorAtualizado) || 0), 0),
    [linhas20sFiltradas]
  );

  const { linhas: linhas20sPagina, paginacao: pag20s } = useMemo(
    () => paginarLinhas(linhas20sFiltradas, pagina20s, limite20s),
    [linhas20sFiltradas, pagina20s, limite20s]
  );

  useEffect(() => {
    setPaginaDetalhe(1);
  }, [filtros, limiteDetalhe, ordenacao]);

  useEffect(() => {
    setPaginaRubricas(1);
  }, [limiteRubricas, filtroRubricas.ativos]);

  useEffect(() => {
    setPagina20s(1);
  }, [limite20s, filtro20s.ativos]);

  useEffect(() => {
    if (aba === "rubricas") setPaginaRubricas(1);
    if (aba === "20s") setPagina20s(1);
  }, [aba]);

  // Cada aba é uma "página" independente: toda entrada nela (inclusive a inicial,
  // já que `aba` começa em "detalhe") busca dado fresco na API e limpa os filtros
  // dinâmicos, em vez de reaproveitar o que já estava carregado na sessão.
  useEffect(() => {
    if (aba === "detalhe") {
      carregar();
    } else if (aba === "rubricas") {
      filtroRubricas.clear();
      setGruposVisiveis(GRUPOS_PADRAO_VISIVEIS);
      carregarRubricas();
    } else if (aba === "20s") {
      filtro20s.clear();
      carregarCalculo20s();
    }
  }, [aba, carregar, carregarRubricas, carregarCalculo20s, filtroRubricas.clear, filtro20s.clear]);

  const alternarGrupoRubricas = useCallback((idGrupo) => {
    setGruposVisiveis((prev) =>
      prev.includes(idGrupo) ? prev.filter((g) => g !== idGrupo) : [...prev, idGrupo]
    );
  }, []);

  const setFiltro = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => setFiltros(filtrosPadrao(agrupado));

  const selecaoProjeto = useMemo(() => selecaoEsocial(linhasFiltradas), [linhasFiltradas]);

  const handleSalvarProjeto = async ({ sobrescreverIdProjeto }) => {
    setSalvandoProjeto(true);
    const res = await salvarProjeto({
      idRelatorioApuracao: Number(id),
      filtros,
      chaves: selecaoProjeto.chaves,
      sobrescreverIdProjeto,
    });
    setSalvandoProjeto(false);

    if (!res.sucesso) {
      setObjSnackBar({ type: "error", msg: res.mensagem || "Não foi possível salvar o projeto." });
      return;
    }

    setModalSalvarProjeto(false);
    const descartados = Array.isArray(res.descartados) ? res.descartados.length : 0;
    const aviso =
      descartados > 0
        ? ` ${descartados} ${descartados === 1 ? "item não entrou" : "itens não entraram"} no projeto (já utilizados ou sem base legal).`
        : "";
    setObjSnackBar({
      type: descartados > 0 ? "warning" : "success",
      msg: `Projeto ${res.codigo} ${res.criado ? "criado" : "atualizado"} com sucesso!${aviso}`,
    });
    carregarProjetosCliente();
  };

  const handleSort = (col) => {
    setOrdenacao((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };

  const handleToggleFlag = useCallback(
    async (codigoRubrica, campo, valor) => {
      setRubricas((prev) =>
        prev.map((r) => (r.codigoRubrica === codigoRubrica ? { ...r, [campo]: valor } : r))
      );
      setAgrupado((prev) =>
        prev.map((l) =>
          l.codigoRubrica === codigoRubrica ? { ...l, [campo]: valor } : l
        )
      );
      setSalvandoRubrica((prev) => ({ ...prev, [codigoRubrica]: true }));

      const res = await atualizarRubricaEsocial(id, codigoRubrica, { [campo]: valor });

      setSalvandoRubrica((prev) => {
        const next = { ...prev };
        delete next[codigoRubrica];
        return next;
      });

      if (!res.sucesso) {
        setRubricas((prev) =>
          prev.map((r) => (r.codigoRubrica === codigoRubrica ? { ...r, [campo]: !valor } : r))
        );
        setAgrupado((prev) =>
          prev.map((l) =>
            l.codigoRubrica === codigoRubrica ? { ...l, [campo]: !valor } : l
          )
        );
      }
    },
    [id]
  );

  /**
   * Base legal da rubrica. Diferente das flags booleanas: propaga também `compoeProjeto`
   * no update otimista, porque é o backend que o deriva — sem isso a tela mostraria a
   * base legal preenchida e a rubrica fora do projeto até o próximo refetch.
   */
  const handleBaseLegalChange = useCallback(
    async (codigoRubrica, baseLegalId) => {
      const descricao =
        baseLegalId == null
          ? null
          : basesLegais.find((b) => b.id === baseLegalId)?.descricao ?? null;
      const patch = {
        baseLegalId,
        baseLegalDescricao: descricao,
        compoeProjeto: baseLegalId != null,
      };
      const rubricasAntes = rubricas;
      const agrupadoAntes = agrupado;

      setRubricas((prev) =>
        prev.map((r) => (r.codigoRubrica === codigoRubrica ? { ...r, ...patch } : r))
      );
      setAgrupado((prev) =>
        prev.map((l) => (l.codigoRubrica === codigoRubrica ? { ...l, ...patch } : l))
      );
      setSalvandoRubrica((prev) => ({ ...prev, [codigoRubrica]: true }));

      const res = await atualizarRubricaEsocial(id, codigoRubrica, { baseLegalId });

      setSalvandoRubrica((prev) => {
        const next = { ...prev };
        delete next[codigoRubrica];
        return next;
      });

      if (!res.sucesso) {
        setRubricas(rubricasAntes);
        setAgrupado(agrupadoAntes);
        setObjSnackBar({
          type: "error",
          msg: res.mensagem || "Não foi possível salvar a base legal.",
        });
        return;
      }

      setObjSnackBar({
        type: "success",
        msg:
          baseLegalId == null
            ? "Base legal removida — a rubrica saiu do projeto."
            : "Base legal salva com sucesso.",
      });
    },
    [agrupado, basesLegais, id, rubricas]
  );

  // Mesmo hook/endpoint de status da sumarização — só o enfileirar muda (desenho 6.3). Um
  // hook por tipo, como na Conciliação SPED: variantes rodam em paralelo no servidor. O
  // arquivo vem de `arquivos[]` do status, então o POST do eSocial devolver 1 só
  // `nomeArquivo` (e o `.zip` do modo por-ano) deixa de importar aqui.
  const notificarExportacao = useCallback(
    ({ severity, msg }) => setObjSnackBar({ type: severity, msg }),
    []
  );
  const exportacaoAgrupado = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "esocial-agrupado",
    enfileirar: () => exportarEsocial(id, { tipo: "agrupado" }),
    onFinalizar: notificarExportacao,
  });
  const exportacaoLancamentos = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "esocial-lancamentos",
    enfileirar: () => exportarEsocial(id, { tipo: "lancamentos" }),
    onFinalizar: notificarExportacao,
  });
  const exportacaoResumoGeral = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "esocial-resumo_geral",
    enfileirar: () => exportarEsocial(id, { tipo: "resumo_geral" }),
    onFinalizar: notificarExportacao,
  });
  // TXM-671: o export da aba Rúbricas leva só as rúbricas que sobraram no filtro e as colunas
  // dos grupos visíveis. `enfileirar` é lido por ref a cada clique (ver o hook), então o
  // closure sempre enxerga o filtro e os grupos do momento.
  const exportacaoRubricas = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "esocial-rubricas",
    enfileirar: () =>
      exportarEsocial(id, {
        tipo: "rubricas",
        formato: "xlsx",
        filtros: {
          codrubr: rubricasFiltradas.map((r) => r.codigoRubrica),
          colunas: colunasVisiveisRubricas(gruposVisiveis),
        },
      }),
    onFinalizar: notificarExportacao,
  });
  const exportacoesPorTipo = {
    agrupado: exportacaoAgrupado,
    lancamentos: exportacaoLancamentos,
    resumo_geral: exportacaoResumoGeral,
  };
  const progressoGatilhoExport = Object.values(exportacoesPorTipo).find((p) => p.ativo);

  const handleExportar = (tipo) => {
    const exportacao = exportacoesPorTipo[tipo];
    if (!exportacao || exportacao.ativo) return;
    setMenuExportOpen(false);
    exportacao.iniciar();
  };

  useEffect(() => {
    if (!menuExportOpen) return undefined;
    const handleClick = (e) => {
      if (menuExportRef.current && !menuExportRef.current.contains(e.target)) {
        setMenuExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuExportOpen]);

  const handleReprocessar = useCallback(async () => {
    if (reprocessando) return;
    const ok = window.confirm(
      "Reprocessar a sumarização desta apuração eSocial?\n\n" +
        "Isso recalcula juntos: Detalhe/Agrupado, Rúbricas e Cálculo 20S.\n" +
        "As flags manuais das rúbricas (compõe projeto, verba férias, incidência) são preservadas."
    );
    if (!ok) return;
    setReprocessando(true);
    const res = await reprocessarSumarizacaoApuracao(id, { termos_exclusao: [] });
    setReprocessando(false);
    setObjSnackBar({
      type: res?.sucesso ? "success" : "error",
      msg: res?.mensagem || (res?.sucesso ? "Reprocessamento enfileirado." : "Falha ao reprocessar."),
    });
    if (res?.sucesso) {
      // Reprocessar recalcula as 3 abas juntas no backend; no frontend, refaz a
      // busca só da aba atualmente visível — as outras se atualizam sozinhas na
      // próxima vez que o usuário entrar nelas (mesmo efeito guiado por `aba`).
      if (aba === "detalhe") carregar();
      else if (aba === "rubricas") {
        filtroRubricas.clear();
        carregarRubricas();
      } else if (aba === "20s") {
        filtro20s.clear();
        carregarCalculo20s();
      }
    }
  }, [
    id,
    reprocessando,
    aba,
    carregar,
    carregarRubricas,
    carregarCalculo20s,
    filtroRubricas.clear,
    filtro20s.clear,
  ]);

  const subtitulo = useMemo(() => {
    const cnpj = formatValue("cnpj", apuracao?.cnpj) || apuracao?.cnpj || "—";
    const ini = apuracao?.periodoInicio || apuracao?.periodo_inicio || "";
    const fim = apuracao?.periodoFim || apuracao?.periodo_fim || "";
    const periodo =
      ini || fim
        ? `${fmtCompetenciaPt(ini) || ini || "—"} – ${fmtCompetenciaPt(fim) || fim || "—"}`
        : "—";
    return `${apuracao?.empresa || "—"} · CNPJ ${cnpj} · Período ${periodo} · Apuração #${id}`;
  }, [apuracao, id]);

  // Só bloqueia a página inteira no primeiro carregamento (sem `apuracao` ainda).
  // Reentradas na aba Detalhe também chamam carregar(), mas atualizam os dados no
  // lugar sem "piscar" a página inteira para um spinner.
  if (loading && apuracao == null) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (erro) {
    return (
      <div className={styles.errorBox}>
        <Alert severity="error">{erro}</Alert>
      </div>
    );
  }

  let compAnterior = null;
  const vistaSecundaria = aba !== "detalhe";
  const tituloPagina =
    aba === "rubricas"
      ? "Rúbricas da folha"
      : aba === "20s"
        ? "Cálculo 20S"
        : `Apuração eSocial — ${apuracao?.empresa || "—"}`;
  const badgePagina =
    aba === "rubricas"
      ? { className: styles.badgeRubricas, label: "INSS · Tabela de rubricas e incidências" }
      : aba === "20s"
        ? { className: styles.badge20s, label: "E-Social · Cálculo 20S com correção Selic" }
        : { className: styles.badgeTipo, label: "eSocial · Folha e CPRB" };

  return (
    <div className={styles.pageWrap}>
      <div className={styles.container}>
        {vistaSecundaria ? (
          <button
            type="button"
            className={styles.linkVoltar}
            onClick={() => setAba("detalhe")}
          >
            ← Voltar aos detalhes da apuração
          </button>
        ) : (
          <Link className={styles.linkVoltar} to="/relatorios/apuracoes">
            ← Voltar para Apurações
          </Link>
        )}

        <header className={styles.pageHeader} role="banner">
          <div className={styles.headerMain}>
            <h1 className={styles.pageTitle}>{tituloPagina}</h1>
            <p className={styles.pageSubtitle}>{subtitulo}</p>
            <span className={badgePagina.className}>{badgePagina.label}</span>
          </div>
          {!vistaSecundaria && (
            <div className={styles.headerActions}>
              <SeletorProjetosCliente
                projetos={projetosCliente}
                onSelecionar={(projeto) =>
                  navigate(montarUrlApuracao(projeto), { state: { pfNonce: Date.now() } })
                }
              />
              {podeEditar && !projetoFiltro && (
                <button
                  type="button"
                  className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                  onClick={() => setModalSalvarProjeto(true)}
                >
                  Salvar Projeto
                </button>
              )}
              {VISTAS_HEADER.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.btnHeader}
                  onClick={() => setAba(item.id)}
                >
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                className={styles.btnHeader}
                onClick={() => setModalAtualizarSelic(true)}
              >
                Atualizar Selic
              </button>
              <button
                type="button"
                className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                disabled={reprocessando}
                onClick={handleReprocessar}
              >
                {reprocessando ? "Reprocessando…" : "Reprocessar"}
              </button>
            </div>
          )}
        </header>

        {aba === "detalhe" && (
          <>
            <div className={styles.dashWrap}>
              <div className={styles.dashGrid}>
                {linhasFiltradas.length === 0 ? (
                  <div className={`${styles.dashCard} ${styles.dashCardVazio}`}>
                    <div className={styles.dashLbl}>Sem totais</div>
                    <p className={styles.dashAlertaTexto}>
                      Nenhum registro no filtro. Amplie o período de competência ou limpe os
                      filtros para recalcular o dashboard.
                    </p>
                  </div>
                ) : (
                  <>
                    <MetricCard
                      label="Total INSS Selic"
                      total={dash.cards.totalInssSelic}
                      serie={dash.serie}
                      campo="valorInssSelic"
                    />
                    <MetricCard
                      label="Total Selic Apurado"
                      total={dash.cards.totalSelicApurado}
                      serie={dash.serie}
                      campo="valorTotalSelic"
                    />
                    <GroupedMetricCard
                      label="Totais por Base Legal"
                      hint="Total Selic apurado, agrupado por base legal no filtro"
                      total={dash.cards.totalSelicApurado}
                      grupos={dash.serieBaseLegal}
                    />
                    <GroupedMetricCard
                      label="Totais por Tipo de Base Legal"
                      hint="Total Selic apurado, agrupado por medida judicial"
                      total={dash.cards.totalSelicApurado}
                      grupos={dash.serieTipo}
                    />
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>Total de Rúbricas</div>
                      <div className={styles.dashHint}>Códigos distintos no filtro</div>
                      <div className={styles.dashVal}>{dash.cards.totalRubricas}</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={styles.sectionListHeader}>
              <h2 className={styles.sectionTitle}>Por competência e rúbrica</h2>
              <div className={styles.toolbar}>
                <span className={styles.toolbarHint}>
                  {totalLancamentos.toLocaleString("pt-BR")} lançamentos no resultado ·{" "}
                  {linhasFiltradas.length} linha{linhasFiltradas.length !== 1 ? "s" : ""} na lista
                </span>
                <div ref={menuExportRef} className={styles.exportMenuWrap}>
                  <BotaoExportacaoProgresso
                    como="nativo"
                    className={styles.btnExportar}
                    progresso={progressoGatilhoExport}
                    rotulo="Exportar"
                    bloquearDuranteJob={false}
                    aria-haspopup="true"
                    aria-expanded={menuExportOpen}
                    onClick={() => setMenuExportOpen((o) => !o)}
                  />
                  {menuExportOpen ? (
                    <div role="menu" className={styles.exportMenu}>
                      {ITENS_MENU_EXPORT.map(({ tipo, rotulo }) => {
                        const p = exportacoesPorTipo[tipo];
                        return (
                          <button
                            key={tipo}
                            type="button"
                            role="menuitem"
                            className={styles.exportMenuItem}
                            aria-disabled={p.ativo || undefined}
                            style={p.ativo ? { cursor: "progress", color: "#666" } : undefined}
                            onClick={() => handleExportar(tipo)}
                          >
                            {p.ativo
                              ? `${rotulo} — ${rotuloDoEstado(p.estado, p.percentual, rotulo)}`
                              : rotulo}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div
              className={`${styles.filtersSection} ${
                !filtrosExpandido ? styles.filtersSectionCollapsed : ""
              }`}
            >
              <button
                type="button"
                className={`${styles.filtersHeader} ${
                  filtrosExpandido ? styles.filtersHeaderExpanded : ""
                }`}
                onClick={() => setFiltrosExpandido((v) => !v)}
                aria-expanded={filtrosExpandido}
                aria-controls="filtros-esocial-body"
              >
                <span className={styles.filtersTitle}>Filtros</span>
                <span
                  className={`${styles.filtersToggleIcon} ${
                    filtrosExpandido ? styles.filtersToggleIconOpen : ""
                  }`}
                  aria-hidden
                >
                  ▼
                </span>
              </button>

              {filtrosExpandido && (
                <div id="filtros-esocial-body" className={styles.filtersBody}>
                  <div className={styles.filtersRow}>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Competência</span>
                      <div className={styles.filterTwin}>
                        <input
                          className={styles.filterInput}
                          type="month"
                          value={filtros.competenciaInicio || ""}
                          onChange={(e) => setFiltro("competenciaInicio", e.target.value)}
                          aria-label="Competência início"
                        />
                        <span className={styles.filterTwinSep}>até</span>
                        <input
                          className={styles.filterInput}
                          type="month"
                          value={filtros.competenciaFim || ""}
                          onChange={(e) => setFiltro("competenciaFim", e.target.value)}
                          aria-label="Competência fim"
                        />
                      </div>
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Cód. rubrica</span>
                      <input
                        className={styles.filterInput}
                        type="text"
                        placeholder="Ex.: 108"
                        value={filtros.codigoRubrica || ""}
                        onChange={(e) => setFiltro("codigoRubrica", e.target.value)}
                        aria-label="Código da rúbrica"
                      />
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Descrição da rubrica</span>
                      <input
                        className={styles.filterInput}
                        type="text"
                        placeholder="Buscar texto"
                        value={filtros.buscaRubrica || ""}
                        onChange={(e) => setFiltro("buscaRubrica", e.target.value)}
                        aria-label="Buscar rúbrica"
                      />
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Tipo de rubrica</span>
                      <select
                        className={styles.filterSelect}
                        value={filtros.tipoRubrica || ""}
                        onChange={(e) => setFiltro("tipoRubrica", e.target.value)}
                        aria-label="Filtro tipo de rubrica"
                      >
                        <option value="">Todos</option>
                        {tiposRubrica.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Incidência da rubrica</span>
                      <select
                        className={styles.filterSelect}
                        value={filtros.flagIncidencia || ""}
                        onChange={(e) => setFiltro("flagIncidencia", e.target.value)}
                        aria-label="Filtro incidência da rubrica"
                      >
                        <option value="">Todos</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                      </select>
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Base legal</span>
                      <FiltroBaseLegalMultiSelect
                        opcoes={opcoesBaseLegal(basesLegais)}
                        selecionadas={filtros.baseLegalSelecionadas || []}
                        onChange={(lista) => setFiltro("baseLegalSelecionadas", lista)}
                      />
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Verbas de afastamento</span>
                      <select
                        className={styles.filterSelect}
                        value={filtros.verbaFerias || ""}
                        onChange={(e) => setFiltro("verbaFerias", e.target.value)}
                        aria-label="Filtro verbas de afastamento"
                      >
                        <option value="">Todos</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                      </select>
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>Projeto</span>
                      <select
                        className={styles.filterSelect}
                        value={projetoFiltro ? String(projetoFiltro) : ""}
                        onChange={(e) => handleProjetoChange(e.target.value)}
                        aria-label="Filtro projeto"
                      >
                        <option value="">Itens disponíveis</option>
                        {projetosAprovados.map((projeto) => (
                          <option key={projeto.idProjeto} value={String(projeto.idProjeto)}>
                            {projeto.codigo}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.filterGroup}>
                      <span className={styles.filterLabel}>&nbsp;</span>
                      <button type="button" className={styles.btnLimpar} onClick={limparFiltros}>
                        Limpar filtros
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {projetoFiltro && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Consulta dos itens do projeto {codigoDoProjeto(projetoFiltro)}. Não é possível editar
                nem salvar projeto neste modo.
              </Alert>
            )}

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <SortTh col="competencia" label="Competência" ordenacao={ordenacao} onSort={handleSort} />
                    <SortTh col="codigoRubrica" label="Cód. rubrica" ordenacao={ordenacao} onSort={handleSort} />
                    <SortTh
                      col="descricaoRubrica"
                      label="Descrição da rubrica"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                    />
                    <SortTh col="tipoRubrica" label="Tipo da rubrica" ordenacao={ordenacao} onSort={handleSort} />
                    <SortTh
                      col="qtdLancamentos"
                      label="Lançamentos"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={styles.num}
                    />
                    <SortTh
                      col="valorBase"
                      label="Valor Base"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorInss"
                      label="Valor INSS"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorTerceiros"
                      label="Terceiros"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorRat"
                      label="RAT"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorTotal"
                      label="Total"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorInssSelic"
                      label="Valor INSS Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorTerceirosSelic"
                      label="Terceiros Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorRatSelic"
                      label="RAT Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorTotalSelic"
                      label="Total Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="aliqSelic"
                      label="Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="valorSelic"
                      label="Valor Selic"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                      className={`${styles.num} ${styles.colValor}`}
                    />
                    <SortTh
                      col="flagIncidencia"
                      label="Incidência da rubrica"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                    />
                    <SortTh
                      col="baseLegal"
                      label="Base legal"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                    />
                    <SortTh
                      col="verbaFerias"
                      label="Verbas de afastamento"
                      ordenacao={ordenacao}
                      onSort={handleSort}
                    />
                    <th scope="col">Projeto</th>
                    <th scope="col">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasPagina.map((l, i) => {
                    const comp = fmtCompetencia(l.competencia);
                    const isStart = comp !== compAnterior;
                    if (isStart) compAnterior = comp;
                    return (
                      <tr
                        key={`${l.codigoRubrica}-${l.competencia}-${i}`}
                        className={isStart ? styles.rowCompStart : undefined}
                      >
                        <td className={styles.compCell}>{fmtCompetenciaPt(l.competencia)}</td>
                        <td className={styles.rubricaCod}>{l.codigoRubrica}</td>
                        <td className={styles.rubricaDesc} title={l.descricaoRubrica || ""}>
                          {l.descricaoRubrica}
                        </td>
                        <td>{l.tipoRubrica ?? "—"}</td>
                        <td className={styles.num}>{l.qtdLancamentos ?? 0}</td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorBase)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorInss)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorTerceiros)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorRat)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorTotal)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorInssSelic)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorTerceirosSelic)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorRatSelic)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorTotalSelic)}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {l.aliqSelic != null
                            ? `${Number(l.aliqSelic).toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}%`
                            : "—"}
                        </td>
                        <td className={`${styles.num} ${styles.colValor}`}>
                          {formatCurrency(l.valorSelic)}
                        </td>
                        <td>
                          <FlagBadge sim={Boolean(l.flagIncidencia)} />
                        </td>
                        <td title={l.baseLegalDescricao || "Sem base legal"}>
                          {l.baseLegalId != null ? (
                            <span className={styles.badgeBaseLegal}>{l.baseLegalDescricao}</span>
                          ) : (
                            celulaOuTraco(l.baseLegalDescricao)
                          )}
                        </td>
                        <td>
                          <FlagBadge sim={Boolean(l.verbaFerias)} />
                        </td>
                        <td>
                          {l.idProjetoUtilizado != null ? codigoDoProjeto(l.idProjetoUtilizado) : "—"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className={styles.btnMini}
                            onClick={() => {
                              const sp = new URLSearchParams();
                              sp.set("codigoRubrica", String(l.codigoRubrica || "").trim());
                              const ym = competenciaParaYm(l.competencia);
                              if (ym) sp.set("competencia", ym);
                              if (l.descricaoRubrica) {
                                sp.set("descricao", String(l.descricaoRubrica).trim());
                              }
                              navigate(
                                `/relatorios/apuracoes/${id}/esocial/lancamentos?${sp.toString()}`
                              );
                            }}
                          >
                            Detalhes
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {linhasPagina.length === 0 && (
                    <tr>
                      <td colSpan={21} className={styles.empty}>
                        Nenhum registro corresponde aos filtros.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagDetalhe.total > 0 && (
              <PaginationBar
                id="eso-pg-size-detalhe"
                paginacao={pagDetalhe}
                limite={limiteDetalhe}
                onLimiteChange={setLimiteDetalhe}
                onPaginaChange={setPaginaDetalhe}
              />
            )}
          </>
        )}

        {aba === "rubricas" && (
          <>
            {rubricas === null ? (
              <div className={styles.loading}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <>
                <FiltroCatalogoDinamico
                  catalog={catalogoRubricas}
                  ativos={filtroRubricas.ativos}
                  onAdd={filtroRubricas.add}
                  onRemove={filtroRubricas.remove}
                  onUpdate={filtroRubricas.update}
                  onClear={filtroRubricas.clear}
                />
                <div className={styles.rubricasToolbar}>
                  <BarraGruposColunas
                    gruposAtivos={gruposVisiveis}
                    onToggle={alternarGrupoRubricas}
                  />
                  <BotaoExportacaoProgresso
                    como="nativo"
                    className={styles.btnExportar}
                    progresso={exportacaoRubricas}
                    rotulo="Exportar"
                    disabled={rubricasFiltradas.length === 0}
                    onClick={() => exportacaoRubricas.iniciar()}
                  />
                </div>
                {escalaVigencia ? (
                  <p className={styles.vigenciaLegenda}>
                    {`Linha do tempo: ${formatarMesAno(escalaVigencia.inicio)} → ${formatarMesAno(
                      escalaVigencia.fim
                    )}`}
                    <span className={styles.legendaAberta}>em aberto</span>
                    <span className={styles.legendaFechada}>encerrada</span>
                  </p>
                ) : null}
                <div className={styles.tableWrap}>
                  <table className={`${styles.table} ${styles.rubricasTable}`}>
                    {/* Cabeçalho em 2 níveis (grupos INSS/FGTS/IRRF). "Base Legal" substitui
                        "Composição do Projeto": a rubrica compõe o projeto exatamente quando
                        tem base legal selecionada. */}
                    <CabecalhoRubricas gruposAtivos={gruposVisiveis} escala={escalaVigencia} />
                    <tbody>
                      {rubricasPagina.map((r) => {
                        const salvando = Boolean(salvandoRubrica[r.codigoRubrica]);
                        return (
                          <tr
                            key={r.codigoRubrica}
                            className={r.baseLegalId != null ? styles.linhaComBaseLegal : undefined}
                          >
                            <td className={styles.rubricaCod} title={celulaOuTraco(r.codigoRubrica)}>
                              {celulaOuTraco(r.codigoRubrica)}
                            </td>
                            <td
                              className={styles.rubricaDesc}
                              title={celulaOuTraco(r.descricaoRubrica)}
                            >
                              {celulaOuTraco(r.descricaoRubrica)}
                            </td>
                            <CelulaVigencia vigencias={r.vigencias} escala={escalaVigencia} />
                            <td title={celulaOuTraco(r.tipoRubrica)}>
                              {celulaOuTraco(r.tipoRubrica)}
                            </td>
                            <CelulasIncidenciaRubrica r={r} gruposAtivos={gruposVisiveis} />
                            <td className={styles.cellFlag}>
                              <FlagSelect
                                value={Boolean(r.flagIncidencia)}
                                disabled={salvando}
                                onChange={(v) =>
                                  handleToggleFlag(r.codigoRubrica, "flagIncidencia", v)
                                }
                                aria-label={`Incidência da Rubrica ${r.codigoRubrica}`}
                              />
                            </td>
                            <td className={styles.cellFlag}>
                              <BaseLegalSelect
                                baseLegalId={r.baseLegalId ?? null}
                                baseLegalDescricao={r.baseLegalDescricao ?? null}
                                basesLegais={basesLegais}
                                disabled={salvando}
                                onChange={(v) => handleBaseLegalChange(r.codigoRubrica, v)}
                                aria-label={`Base Legal ${r.codigoRubrica}`}
                              />
                            </td>
                            <td className={styles.cellFlag}>
                              <FlagSelect
                                value={Boolean(r.verbaFerias)}
                                disabled={salvando}
                                onChange={(v) =>
                                  handleToggleFlag(r.codigoRubrica, "verbaFerias", v)
                                }
                                aria-label={`Verbas de Afastamento ${r.codigoRubrica}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {rubricasPagina.length === 0 && (
                        <tr>
                          <td
                            colSpan={colunasVisiveisRubricas(gruposVisiveis).length + 3}
                            className={styles.empty}
                          >
                            Nenhuma rubrica encontrada com os filtros.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  id="eso-pg-size-rubricas"
                  paginacao={pagRubricas}
                  limite={limiteRubricas}
                  onLimiteChange={setLimiteRubricas}
                  onPaginaChange={setPaginaRubricas}
                />
              </>
            )}
          </>
        )}

        {aba === "20s" && (
          <>
            {calculo20s === null ? (
              <div className={styles.loading}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <>
                <div className={styles.dashWrap}>
                  <div className={styles.dashGrid}>
                    <div className={`${styles.dashCard} ${styles.dashCardWide}`}>
                      <div className={styles.dashLbl}>Valor Atualizado (soma no filtro)</div>
                      <div className={styles.dashVal}>
                        {formatCurrency(valorAtualizado20sFiltrado)}
                      </div>
                    </div>
                  </div>
                </div>
                <FiltroCatalogoDinamico
                  catalog={catalogo20s}
                  ativos={filtro20s.ativos}
                  onAdd={filtro20s.add}
                  onRemove={filtro20s.remove}
                  onUpdate={filtro20s.update}
                  onClear={filtro20s.clear}
                />
                <div className={styles.tableWrap}>
                  <table className={styles.table} style={{ minWidth: 900 }}>
                    <thead>
                      <tr>
                        <th>Competência</th>
                        <th className={styles.num}>Base de Cálculo</th>
                        <th className={styles.num}>Diferença Base</th>
                        <th className={styles.num}>Cálculo 20S</th>
                        <th className={styles.num}>Taxa Selic</th>
                        <th className={styles.num}>Acréscimo Selic</th>
                        <th className={styles.num}>Valor Atualizado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas20sPagina.map((l) => (
                        <tr key={l.competencia}>
                          <td className={styles.compCell}>{fmtCompetenciaPt(l.competencia)}</td>
                          <td className={styles.num}>{formatCurrency(l.valorBaseCalculo)}</td>
                          <td className={styles.num}>
                            {formatCurrency(l.valorDiferencaBaseCalculada)}
                          </td>
                          <td className={styles.num}>{formatCurrency(l.valorCalculo20s)}</td>
                          <td className={styles.num}>
                            {l.aliqSelic != null ? `${l.aliqSelic}%` : "—"}
                          </td>
                          <td className={styles.num}>{formatCurrency(l.valorAcrescimoSelic)}</td>
                          <td className={styles.num}>{formatCurrency(l.valorAtualizado)}</td>
                        </tr>
                      ))}
                      {linhas20sPagina.length === 0 && (
                        <tr>
                          <td colSpan={7} className={styles.empty}>
                            Nenhuma base de cálculo 20S para o período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  id="eso-pg-size-20s"
                  paginacao={pag20s}
                  limite={limite20s}
                  onLimiteChange={setLimite20s}
                  onPaginaChange={setPagina20s}
                />
              </>
            )}
          </>
        )}
      </div>

      <ModalSalvarProjeto
        open={modalSalvarProjeto}
        onClose={() => setModalSalvarProjeto(false)}
        empresa={{ razaoSocial: apuracao?.empresa, cnpj: apuracao?.cnpj }}
        periodoLabel={`${fmtMesAno(filtros.competenciaInicio)} – ${fmtMesAno(filtros.competenciaFim)}`}
        selecao={selecaoProjeto.resumo}
        projetosEmPrevia={projetosEmPrevia}
        salvando={salvandoProjeto}
        onConfirmar={handleSalvarProjeto}
      />
      <ModalAtualizarSelicApuracao
        open={modalAtualizarSelic}
        idApuracao={id}
        apuracao={apuracao}
        onClose={() => setModalAtualizarSelic(false)}
        onSuccess={(msg) => {
          setObjSnackBar({ type: "success", msg });
          navigate("/relatorios/apuracoes");
        }}
        onError={(msg) => setObjSnackBar({ type: "error", msg })}
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default ApuracaoDetalheEsocial;
